import { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CropFreeRoundedIcon from '@mui/icons-material/CropFreeRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import ZoomInMapRoundedIcon from '@mui/icons-material/ZoomInMapRounded';
import {
  Alert, Box, Chip, CircularProgress, Drawer, IconButton, Stack, Tooltip, Typography
} from '@mui/material';
import { archiveApi } from '../api/client';
import { usePageImage } from '../api/usePageImage';
import type { PageHighlights, PageView } from '../api/types';
import { HighlightedPage } from './HighlightedText';
import { PageCanvas } from './PageCanvas';
import { PrecisionBadge } from './PrecisionBadge';

interface ViewerTarget {
  pdfId: number;
  page: number;
}

interface PageViewerProps {
  target: ViewerTarget | null;
  /** The raw query. The server resolves it to polygons; the client never parses it. */
  query: string;
  terms: string[];
  onClose: () => void;
  onChange: (target: ViewerTarget) => void;
}

export function PageViewer({ target, query, terms, onClose, onChange }: PageViewerProps) {
  const [page, setPage] = useState<PageView | null>(null);
  const [marks, setMarks] = useState<PageHighlights | null>(null);
  const [active, setActive] = useState(0);
  // The whole page by default. Zooming to the hit is available on demand, but it
  // must not be what a reader is given first: the page as a whole is what says
  // which document this is.
  const [zoomed, setZoomed] = useState(false);
  const [error, setError] = useState('');
  const imageUrl = usePageImage(target?.pdfId, target?.page);

  useEffect(() => {
    if (!target) return;
    const controller = new AbortController();

    archiveApi.page(target.pdfId, target.page, controller.signal)
      .then(setPage)
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : String(reason));
      });

    // Geometry is a second request on purpose: the page text and its coordinates
    // come from different tables and the text should not wait for the polygons.
    archiveApi.highlights(target.pdfId, target.page, query, controller.signal)
      .then(setMarks)
      .catch(() => undefined);

    return () => controller.abort();
  }, [target, query]);

  const hits = useMemo(() => marks?.highlights ?? [], [marks]);

  const step = useCallback((delta: number) => {
    if (!hits.length) return;
    setActive((i) => (i + delta + hits.length) % hits.length);
  }, [hits.length]);

  // Archive research is repetitive: stepping through hits and pages has to work
  // from the keyboard or the mouse becomes the bottleneck.
  useEffect(() => {
    if (!target) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'n' || event.key === 'ArrowRight') step(1);
      if (event.key === 'p' || event.key === 'ArrowLeft') step(-1);
      if (event.key === 'f') setZoomed((z) => !z);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [target, step]);

  const canvas = page && marks && marks.render.width > 0 && target ? (
    <PageCanvas
      imageUrl={imageUrl}
      width={marks.render.width}
      height={marks.render.height}
      highlights={hits}
      activeIndex={hits.length ? active : undefined}
      focusIndex={zoomed && hits.length ? active : undefined}
      focusPad={0.14}
      onSelect={setActive}
      alt={`Seite ${target.page} von ${page.file_name}`}
    />
  ) : null;

  return (
    <Drawer
      anchor="right"
      open={Boolean(target)}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', md: 'min(94vw, 1560px)' }, backgroundColor: 'background.default' } } }}
    >
      <Stack sx={{ height: '100dvh' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          sx={{ minHeight: 72, px: { xs: 1.5, sm: 2.5 }, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3" noWrap>{page?.file_name ?? 'Dokument wird geladen …'}</Typography>
            {page && (
              <Typography variant="caption" color="text.secondary">
                Seite {page.page_number} von {page.page_count}
                {page.folder_path !== '.' && ` · ${page.folder_path}`}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton aria-label="Vorherige Seite" disabled={!page || page.page_number <= 1} onClick={() => page && onChange({ pdfId: page.pdf_id, page: page.page_number - 1 })}>
              <ArrowBackRoundedIcon />
            </IconButton>
            <IconButton aria-label="Nächste Seite" disabled={!page || page.page_number >= page.page_count} onClick={() => page && onChange({ pdfId: page.pdf_id, page: page.page_number + 1 })}>
              <ArrowForwardRoundedIcon />
            </IconButton>
            <IconButton aria-label="Schließen" onClick={onClose}><CloseRoundedIcon /></IconButton>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : !page || !target ? (
          <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.2fr) minmax(360px, .8fr)' } }}>
            <Stack sx={{ minHeight: { xs: '52dvh', md: 0 }, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(2, 6, 23, 0.72)' : '#E9ECF5' }}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
              >
                <IconButton size="small" aria-label="Vorheriger Treffer" disabled={hits.length < 2} onClick={() => step(-1)}>
                  <KeyboardArrowLeftRoundedIcon />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 96, textAlign: 'center' }}>
                  {hits.length ? `Treffer ${active + 1} / ${hits.length}` : 'Kein Treffer'}
                </Typography>
                <IconButton size="small" aria-label="Nächster Treffer" disabled={hits.length < 2} onClick={() => step(1)}>
                  <KeyboardArrowRightRoundedIcon />
                </IconButton>

                <Tooltip title={zoomed ? 'Ganze Seite zeigen (f)' : 'Auf den Treffer zoomen (f)'}>
                  <span>
                    <IconButton size="small" onClick={() => setZoomed((z) => !z)} disabled={!hits.length}>
                      {zoomed ? <CropFreeRoundedIcon /> : <ZoomInMapRoundedIcon />}
                    </IconButton>
                  </span>
                </Tooltip>

                <Box sx={{ ml: 'auto' }}>
                  <PrecisionBadge status={marks?.highlight_status} hits={hits} active={active} />
                </Box>
              </Stack>

              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: { xs: 1, sm: 2 } }}>
                {canvas ?? (
                  <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
                    <CircularProgress size={22} />
                  </Box>
                )}
              </Box>
            </Stack>

            <Box sx={{ overflow: 'auto', p: { xs: 2, sm: 3 }, borderLeft: { md: '1px solid' }, borderTop: { xs: '1px solid', md: 0 }, borderColor: 'divider', backgroundColor: 'background.paper' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="overline" color="text.secondary">Transkription</Typography>
                {marks?.truncated && <Chip size="small" variant="outlined" label="Treffer gekürzt" />}
              </Stack>
              <Typography component="div" sx={{ mt: 1, whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 14, lineHeight: 1.75 }}>
                {page.text
                  ? <HighlightedPage text={page.text} terms={terms} />
                  : <Typography color="text.secondary">Für diese Seite ist noch kein Text verfügbar.</Typography>}
              </Typography>
            </Box>
          </Box>
        )}
      </Stack>
    </Drawer>
  );
}
